import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ElementRef,
  ChangeDetectorRef,
  HostBinding,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageProcessorConfig, ResizePipe } from '@teamfoster/sdk/image';
@Component({
  selector: 'app-image',
  templateUrl: './image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImageComponent implements OnInit {
  private sanitizer = inject(DomSanitizer);

  private imageConfig = inject(ImageProcessorConfig);

  readonly loaded = output<boolean>();

  readonly debug = input<boolean>(false); // Shows debugdata in table

  readonly src = input.required<string>(); // URL without resizing pipes etc.
  readonly alt = input.required<string>(); // Regular Alt text

  readonly loading = input('lazy'); // Lazy loading

  // When both width and height are filled, this is used to calculate the aspect-ratio
  readonly width = input<number>(0);
  readonly height = input<number>(0);

  // if image get's clipped, this determines from what anchor
  readonly anchor = input<
    'topleft' | 'topcenter' | 'topright' | 'middleleft' | 'middlecenter' | 'middleright' | 'bottomleft' | 'bottomcenter' | 'bottomright'
  >('middlecenter');

  // Type of clipping when aspect ratio is given.
  readonly mode = input<'crop' | 'stretch' | 'pad' | 'max'>('crop');

  // Image file format
  readonly format = input<'jpg' | 'png' | 'bmp' | 'gif' | 'webp'>('png');

  // Scale
  readonly scale = input<'both' | 'down' | 'canvas'>('both');

  // Set what image sizes the srcset will contain (this will be used to pick from)
  readonly imageSizes = input<number[]>([550, 800, 1200, 1600, 1920]);

  // How many cols does the image span on what breakpoint?
  // For example: { md: 4, xl: 3 } is the same as bootstrap .col-md-4 .col-xl-3 classes
  readonly sizes = input<{
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  }>({});

  // Overwrite the sizes attribute (to set it manually)
  readonly customSizes = input<string>('');

  // Used only for debugging purposes
  readonly img = viewChild<ElementRef<HTMLImageElement>>('img');

  // Inherited from CSS: $grid-breakpoints
  // is used to generate size attribute with collspans
  cssBreakPoints = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400,
  };

  gcd: number = 0; // GCD is the highest number that evenly divides both numbers
  aspectRatio: string = 'auto'; // used for CSS, outputs an string as such: '16/9' or 'auto'
  currentSrc: string | undefined; // on resize this will be updated with the chosen image url

  private resize = new ResizePipe(this.imageConfig);

  ngOnInit(): void {
    this.gcd = this.aspect(this.height(), this.width());
    const height = this.height();
    const width = this.width();
    this.aspectRatio = width && height ? `${width / this.gcd} / ${height / this.gcd}` : 'auto';

    // required alt text
    if (!this.alt()) {
      console.error(`Attribute 'alt' is required for image with src ${this.src()}`);
    }
  }

  //? enable this if you want to test with chosen img size
  @HostBinding('attr.style')
  public get getCSSvariables(): any {
    return this.sanitizer.bypassSecurityTrustStyle('--aspect-ratio:' + this.aspectRatio);
  }

  // Test purposes only
  // shows which src gets chosen
  // @HostListener('window:resize', ['$event'])
  // onResize() {
  //   this.currentSrc = this.img.nativeElement.currentSrc.replace(this.src, '');
  //   this.cd.markForCheck();
  // }

  get sizesAttr() {
    // So youll be able to make your own size property build
    const customSizes = this.customSizes();
    if (customSizes) {
      return customSizes;
    }
    const sizes = this.sizes();
    if (!sizes) {
      return 'auto';
    }

    const sizeRules: string[] = [];

    // Sets for each container width an srcset (so for full width images)
    Object.keys(sizes).forEach(key => {
      const size = this.sizes()[key as keyof typeof sizes];
      const breakpoint = this.cssBreakPoints[key as keyof typeof this.cssBreakPoints];

      if (!size || !breakpoint) {
        return;
      }

      const width = (100 / 12) * size;

      sizeRules.push(`(min-width: ${breakpoint}px) ${width}vw`); // TODO calculate width with max width of container & vw?
    });

    return sizeRules.join(',').concat(', 100vw');
  }

  get srcSetAttr() {
    const srcSetRules: string[] = [];

    this.imageSizes().forEach(imgWidth => {
      srcSetRules.push(
        `${this.resize.transform(
          this.src(),
          imgWidth,
          this.getAspectRatioHeight(imgWidth),
          this.anchor(),
          this.mode(),
          this.format(),
          this.scale()
        )}  ${imgWidth}w` // TODO calculate width with max width of container & vw?
      );
    });

    return srcSetRules.join(',');
  }

  getAspectRatioHeight(size: number): number {
    const height = this.height();
    const width = this.width();
    if (!height || !width) {
      return 0;
    }

    // ? What if only height is given ?
    const percentageDiff = (size - width) / width;
    const newHeight = Math.round((percentageDiff + 1) * height);

    return newHeight;
  }

  aspect(h: number, w: number): number {
    return !w ? h : this.aspect(w, h % w);
  }
}
